/**
 * The HTTP contract between every client and the backend.
 *
 * This file is the seam that lets the backend and frontend lanes be built
 * independently — both compile against it before either is implemented, which is
 * the whole reason it ships before any behaviour (#2).
 *
 * It lives in `@sportsos/engine` rather than a fourth workspace because that is
 * the package every surface already imports, and ADR-0012 chose fewer packages
 * over tidier naming. The engine itself never reads these types.
 *
 * No vendor types appear here. The frontend's only configuration is the API base
 * url; it knows nothing of the database or the auth provider (ADR-0008).
 */
import type { BatterHand, BowlingArm, BowlingType, Event } from "./events.ts";
import type { EventId, InningsNo, MatchId, PlayerId, RuleSetId, Shortcode, TeamId, TournamentId, UserId } from "./ids.ts";
import type { RuleSet } from "./ruleset.ts";
import type { AnomalyRef, MatchStatus, OverCount } from "./state.ts";

// ── Errors ──────────────────────────────────────────────────────────────────

export type ApiErrorCode =
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "validation_failed"
  | "conflict"
  | "anomalies_unresolved"
  | "rate_limited"
  /** Declared in the contract but the ticket that builds it has not landed. */
  | "not_implemented"
  /** A service we depend on (YouTube) refused or failed. Retrying may work. */
  | "upstream_unavailable"
  | "internal";

export interface ApiError {
  readonly code: ApiErrorCode;
  readonly message: string;
  /** Field-level detail for `validation_failed`. */
  readonly fields: Readonly<Record<string, string>> | null;
}

// ── Auth (#4) ───────────────────────────────────────────────────────────────

/**
 * Session responses carry no token. Auth is server-side and the session lives in
 * an httpOnly cookie, because a JWT in `localStorage` is readable by any XSS
 * (ADR-0008).
 */
export interface AuthUser {
  readonly id: UserId;
  readonly displayName: string;
  readonly email: string | null;
  readonly phone: string | null;
}

export interface SignUpRequest {
  readonly email: string;
  readonly password: string;
  readonly displayName: string;
}

export interface SignInRequest {
  readonly email: string;
  readonly password: string;
}

export interface SendOtpRequest {
  readonly phone: string;
}

export interface VerifyOtpRequest {
  readonly token: string;
  readonly phone?: string;
  readonly displayName?: string;
}

export interface Msg91WidgetConfig {
  readonly widgetId: string;
  readonly tokenAuth: string;
}

export interface UpdateProfileRequest {
  readonly displayName?: string;
  readonly phone?: string;
  readonly email?: string;
}

export interface MeResponse {
  readonly user: AuthUser;
  /**
   * The account's playing identity, or null when it has none.
   *
   * An account and a player are separate rows (PRD ID-3), so "who am I as a player" is
   * not `user.id` and never was — reading stats with a `UserId` queries `players` for an
   * id that lives in `users` and finds nothing. Carrying the answer on `me` fixes that
   * once for every screen instead of at each call site.
   *
   * Resolved by a read-only lookup, so it stays null for an organiser who has never been
   * squadded. Minting a player here would hand a playing identity to every scorer who
   * merely opened the app, which is what the "am I playing in this team?" question at
   * team creation exists to avoid.
   */
  readonly playerId: PlayerId | null;
}

/** Sign-up and sign-in return the same shape; the session arrives as a cookie. */
export type SessionResponse = MeResponse;

/**
 * `POST /api/auth/app/exchange`: the code a Google sign-in handed the streamer app, and the
 * PKCE verifier it was bound to (ADR-0020).
 */
export interface AppCodeExchangeRequest {
  readonly code: string;
  readonly verifier: string;
}

/** Every mutation with nothing to say returns this rather than an empty body. */
export interface OkResponse {
  readonly ok: true;
}

// ── Players and teams (#5) ──────────────────────────────────────────────────

export interface Player {
  readonly id: PlayerId;
  readonly displayName: string;
  readonly claimedByUserId: UserId | null;
  readonly phone: string | null;
  readonly email: string | null;
  /**
   * How this player bats and bowls, as last observed (ADR-0023). These are **defaults**: what the
   * scorer's walk-in and bowler sheets are pre-filled with. What was true in a given match is in
   * that match's ledger, and no scorecard or wheel reads these. Null until known.
   *
   * Optional so a response cached by an older build still reads: absent means not known.
   */
  readonly battingHand?: BatterHand | null;
  readonly bowlingArm?: BowlingArm | null;
  readonly bowlingType?: BowlingType | null;
  /**
   * Which of the three the player set himself, on a claimed profile. A scorer's choice never
   * overwrites one of these on the profile; it stays in that match's ledger (ADR-0023).
   */
  readonly styleSelfSet?: readonly PlayerStyleField[];
}

/** The three things a profile says about how someone plays. */
export type PlayerStyleField = "battingHand" | "bowlingArm" | "bowlingType";

/**
 * A player's saved style, for the profile screen (ADR-0023). Null means not recorded yet, and the
 * screen says so rather than showing right-handed.
 */
export interface PlayerStyle {
  readonly battingHand: BatterHand | null;
  readonly bowlingArm: BowlingArm | null;
  readonly bowlingType: BowlingType | null;
  /** True when the signed-in account has claimed this player, and so may change it here. */
  readonly editable: boolean;
}

/**
 * `PUT /api/players/:id/style`: the owner of a claimed profile sets how they play.
 *
 * Only the fields present are changed; `null` clears one. What is set here is marked as the
 * player's own word, so no scorer's later choice overwrites it. Past matches do not move.
 */
export interface UpdatePlayerStyleRequest {
  readonly battingHand?: BatterHand | null;
  readonly bowlingArm?: BowlingArm | null;
  readonly bowlingType?: BowlingType | null;
}

/**
 * Turn a search result into a player that can actually be put in a squad.
 *
 * Global search answers with two kinds of row: players, and registered accounts that have
 * never been scored — the latter so a captain can find a team-mate by phone before that
 * person has ever batted. An account row carries a `users.id`, and everything that
 * references a person (`team_players`, `match_players`) references `players`, so the id
 * has to be exchanged for a real one before it is used. Doing that here, when the row is
 * picked, means the rest of the app never has to know which kind it started as.
 *
 * Idempotent: an id that is already a player comes straight back, and an account that
 * already has a player resolves to that same player rather than a second one.
 */
export interface ResolvePlayerRequest {
  /** A `PlayerId` or a `UserId` — whichever the search result carried. */
  readonly id: string;
}

export interface CreatePlayerRequest {
  readonly displayName: string;
  readonly phone?: string | null;
  readonly email?: string | null;
}

export interface SearchPlayersQuery {
  readonly q: string;
  readonly scope?: "own" | "global";
}

/**
 * Collections are wrapped in an object, never returned as a bare array. Both lanes
 * compile against the same envelope, so the frontend cannot assume `Player[]` while
 * the backend returns `{ players }` — a mismatch that typechecks green on both
 * sides and breaks only at integration.
 */
export interface PlayersResponse {
  readonly players: readonly Player[];
}

export interface TeamsResponse {
  readonly teams: readonly Team[];
}

export interface MatchesResponse {
  readonly matches: readonly MatchListItem[];
}

/**
 * The formats this scorer may pin a match to.
 *
 * The only way a client can obtain a `ruleSetId`. It must not invent one: `rulesets.id` is
 * a database default and so differs per environment, and matching on a *name* instead
 * would defeat the point of pinning by id at all (PRD §6.5).
 */
export interface CreateRuleSetRequest {
  readonly name: string;
  readonly overs: number | null;
  readonly ballsPerOver: number;
  readonly playersPerSide: number;
  readonly maxOversPerBowler: number | null;
  readonly lbwEnabled: boolean;
  readonly noBallFreeHit: boolean;
  readonly wideRule: "standard" | "leg_side_only" | "none";
  readonly superOverOnTie: boolean;
  readonly lastManStands: boolean;
  readonly oneTipOneHandOut: boolean;
  readonly oneBounceCatchOut: boolean;
}

export interface RuleSetsResponse {
  readonly ruleSets: readonly RuleSet[];
}

export interface Team {
  readonly id: TeamId;
  readonly name: string;
  /** The saved squad, so a second match is a re-selection not a re-entry (MS-2). */
  readonly squad: readonly Player[];
  /**
   * Whether the caller created this team, as opposed to merely playing for it.
   *
   * "My teams" lists both — a player added by his captain creates nothing, and listing only
   * what you own is what made that screen empty for exactly the people it was meant to
   * serve. Changing a team is still the owner's alone, so the controls key off this.
   */
  readonly isOwner: boolean;
}

export interface CreateTeamRequest {
  readonly name: string;
  readonly playerIds: readonly PlayerId[];
  /**
   * Whether the creator is playing for this team, and so whether they become its captain.
   *
   * Asked rather than inferred. One account is both personas: a neutral scorer recording a
   * match between two clubs plays for neither, and a club captain setting up his own game
   * plays for one — and nothing about *where* the team is created tells you which, because
   * both create teams from match setup. Guessing is wrong for one of them every time.
   *
   * Omitted means false, so the existing match-setup path keeps behaving as it does.
   */
  readonly imPlaying?: boolean;
}

/**
 * Add players to a team's saved squad.
 *
 * **Additive, never a replacement**, which is why it is not `CreateTeamRequest` reused as a
 * PUT. A scorer who brings a substitute for one match has not said the regulars are gone,
 * and a whole-squad write would let one afternoon's XI delete nine players from the team —
 * eroding the saved squad MS-2 depends on, a match at a time. P0 has no way to remove a
 * player, deliberately: a squad with one name too many costs an untick, one missing nine
 * costs re-typing them.
 */
export interface AddTeamPlayersRequest {
  readonly playerIds: readonly PlayerId[];
}

// ── Team invites ────────────────────────────────────────────────────────────

export interface TeamInvite {
  readonly code: string;
  readonly teamId: TeamId;
  readonly teamName?: string;
  readonly expiresAt: string | null;
  readonly maxUses: number | null;
  readonly useCount: number;
}

export interface CreateTeamInviteRequest {
  readonly maxUses?: number | null;
  readonly expiresInHours?: number | null;
}

export interface TeamInviteResponse {
  readonly invite: TeamInvite;
}

export interface JoinTeamResponse {
  readonly teamId: TeamId;
  readonly teamName: string;
}

export interface InvitePreviewResponse {
  readonly teamName: string;
  readonly code: string;
}

// ── Match setup (#5) ────────────────────────────────────────────────────────

export interface MatchSquadEntry {
  readonly playerId: PlayerId;
  readonly battingOrder: number;
  readonly inXi: boolean;
}

/**
 * Replace one side's squad and batting order.
 *
 * A whole-side replacement rather than a patch, because a batting order is a
 * permutation: sending "move Rohit to 3" leaves the server guessing what happened
 * to everyone below him, and two scorers reordering at once would interleave into
 * an order neither chose. The lineup stays editable mid-innings (PRD MS-4).
 */
export interface UpdateSquadRequest {
  readonly teamId: TeamId;
  readonly squad: readonly MatchSquadEntry[];
}

/**
 * Whether other people see this match in their Live now / Recent lists. The match's own
 * link (`/m/{shortcode}`) works either way — this is discoverability, not access control.
 */
export interface SetMatchVisibilityRequest {
  readonly public: boolean;
}

export interface CreateMatchRequest {
  readonly teamAId: TeamId;
  readonly teamBId: TeamId;
  /** Referenced by id so a later rule edit cannot rewrite this match (PRD §6.5). */
  readonly ruleSetId: RuleSetId;
  readonly tossWinnerTeamId: TeamId;
  readonly tossDecision: "bat" | "bowl";
  readonly squads: Readonly<Record<string, readonly MatchSquadEntry[]>>;
  /** Discoverable on the home screen. Defaults to `true` if omitted. */
  readonly public?: boolean;
  /** When created as part of a tournament, links back to it. */
  readonly tournamentId?: TournamentId;
}

export interface Match {
  readonly id: MatchId;
  readonly shortcode: Shortcode;
  readonly status: MatchStatus;
  readonly ruleSet: RuleSet;
  readonly teamA: Team;
  readonly teamB: Team;
  readonly tossWinnerTeamId: TeamId | null;
  readonly tossDecision: "bat" | "bowl" | null;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  /** Discoverable in the live match index. Defaults to `true`. */
  readonly public: boolean;
  /** Non-null when this match belongs to a tournament. */
  readonly tournamentId: TournamentId | null;
  /** The user who created this match. Absent on older responses. */
  readonly createdByUserId?: UserId;
}

/**
 * A row in the my-matches list. Built from `match_summary`, never by replaying —
 * a 50-match list would otherwise replay ~12,500 events (ADR-0002).
 */
export interface InningsSummary {
  readonly runs: number;
  readonly wickets: number;
  readonly overs: string;
  readonly superOver?: boolean;
  readonly battingTeamName?: string;
}

export interface MatchListItem {
  readonly id: MatchId;
  readonly shortcode: Shortcode;
  readonly status: MatchStatus;
  readonly teamAName: string;
  readonly teamBName: string;
  readonly summaryLine: string;
  readonly resultText: string | null;
  readonly innings: readonly InningsSummary[];
  readonly ruleSetName: string | null;
  readonly updatedAt: string;
}

// ── The ledger (#6) ─────────────────────────────────────────────────────────

export interface GetEventsResponse {
  readonly matchId: MatchId;
  readonly ruleSet: RuleSet;
  /** The whole ledger, superseded and voided entries included — `replay()` resolves
   *  them, and a client that filtered first would get the wrong scorecard. */
  readonly events: readonly Event[];
  /**
   * Missing `seq` ranges across the whole match.
   *
   * On the read path too, because a hole must be *discoverable* and not merely
   * mentioned once. Reported only on the append that happened to touch that innings,
   * a device hydrating after a handoff would be told everything was fine while balls
   * were missing — and PRD §7.2 wants a gap to be a visible error, not a
   * single-response one.
   */
  readonly gaps: readonly SeqGap[];
}

export interface AppendEventsRequest {
  readonly events: readonly Event[];
}

/**
 * Why the server refused an event. Restricted to cases where the client cannot be
 * right: **cricket-illegal is not on this list**. If the two engines disagree that
 * is version skew, and destroying a scorer's work over our own deploy bug is
 * indefensible (ADR-0011). Enforcement lives in the client, at entry time.
 */
export type RejectionReason =
  | "malformed"
  | "unknown_match"
  | "unauthorized"
  | "seq_conflict"
  /**
   * The database refused the row for a reason the client must fix — a `supersedes`
   * naming an event that is not in the ledger, a value out of range for its column, a
   * NUL byte inside the payload.
   *
   * Exists because the alternative was worse: without it such an event escaped as a
   * 500, and since the client marks nothing synced without its own ack (ADR-0010) it
   * retried the identical batch forever. A class of retry that can never succeed is
   * the exact opposite of "retries are free", so an unstorable event is named and
   * rejected rather than left to wedge the outbox.
   */
  | "unstorable";

/** The event is durably stored. `duplicate` means a retry landed — also success. */
export interface AcceptedAck {
  readonly eventId: EventId;
  readonly status: "stored" | "duplicate";
  /** Non-null when the server's engine thinks this delivery is illegal. Stored
   *  anyway; blocks completion until reviewed (ADR-0011). */
  readonly anomaly: string | null;
}

export interface RejectedAck {
  readonly eventId: EventId;
  readonly status: "rejected";
  readonly reason: RejectionReason;
  readonly message: string;
  /**
   * For `seq_conflict`: the event already occupying that `(inningsNo, seq)` address.
   *
   * Typed on the wire rather than only mentioned in `message`, because the outbox has to
   * *branch* on it and parsing an id back out of a human sentence is the kind of coupling
   * that breaks the first time someone rewords the message.
   *
   * It tells the two conflict causes apart, and they need opposite responses. If the
   * address is held by an event this device already has, the client renumbered wrongly —
   * usually after undoing a ball the server had already stored — and the fix is to take
   * the next free `seq` and resend. If it is held by an id this device has never seen,
   * a second device is writing the same innings, which is a reconciliation problem no
   * amount of retrying fixes.
   *
   * Null for every other rejection reason.
   */
  readonly heldBy: EventId | null;
}

export type EventAck = AcceptedAck | RejectedAck;

/**
 * Named members and this guard exist because the outbox's central decision is
 * "may I drop this event from the queue?" — and `Array.prototype.filter` does not
 * narrow a union on its own. Without the predicate, #13 would either cast or
 * re-derive the check, and a mistake there loses a ball.
 */
export const isAccepted = (ack: EventAck): ack is AcceptedAck => ack.status !== "rejected";

/** A hole in an innings' `seq`. Loud and explicit, never silently tolerated. */
export interface SeqGap {
  readonly inningsNo: InningsNo;
  readonly fromSeq: number;
  readonly toSeq: number;
}

/**
 * Acknowledged **per event**, never once for the batch. The client marks an event
 * synced only on its own ack — a 2xx for the batch is not evidence that every
 * event in it landed (ADR-0010).
 */
export interface AppendEventsResponse {
  readonly acks: readonly EventAck[];
  readonly gaps: readonly SeqGap[];
}

// ── Completion (#9) ─────────────────────────────────────────────────────────

export interface CompleteMatchResponse {
  readonly status: "completed";
  readonly resultText: string;
}

/** 409 body when completion is refused. Each entry links to a correction sheet. */
export interface AnomaliesUnresolvedError extends ApiError {
  readonly code: "anomalies_unresolved";
  readonly anomalies: readonly AnomalyRef[];
}

/** A reason is mandatory. The audit trail is the entire point (ADR-0005). */
export interface ReopenMatchRequest {
  readonly reason: string;
}

// ── Tournaments (P1, #37) ──────────────────────────────────────────────

export type TournamentFormat = "round_robin" | "groups_knockout";
export type FixtureStatus = "upcoming" | "live" | "completed" | "abandoned" | "no_result";

export interface CreateTournamentRequest {
  readonly name: string;
  readonly description: string | null;
  readonly startDate: string;
  readonly endDate: string;
  readonly format: TournamentFormat;
  readonly ruleSetId: RuleSetId;
  /** Number of groups for `groups_knockout`. Ignored for `round_robin`. */
  readonly groupCount: number | null;
  readonly public: boolean;
}

export interface Tournament {
  readonly id: TournamentId;
  readonly shortcode: Shortcode;
  readonly name: string;
  readonly description: string | null;
  readonly startDate: string;
  readonly endDate: string;
  readonly format: TournamentFormat;
  readonly ruleSet: RuleSet;
  readonly public: boolean;
  readonly createdBy: UserId;
  readonly createdAt: string;
}

export interface TournamentsResponse {
  readonly tournaments: readonly Tournament[];
}

export interface TournamentDetailResponse {
  readonly tournament: Tournament;
  readonly fixtures: readonly Fixture[];
  readonly teams: readonly TournamentTeam[];
}

export interface TournamentTeam {
  readonly teamId: TeamId;
  readonly name: string;
  readonly group: string | null;
  readonly seed: number | null;
}

export interface AddTournamentTeamsRequest {
  readonly teamIds: readonly TeamId[];
}

export interface Fixture {
  readonly matchId: MatchId;
  readonly round: number;
  readonly matchNumber: number;
  readonly teamAId: TeamId | null;
  readonly teamBId: TeamId | null;
  readonly teamAName: string | null;
  readonly teamBName: string | null;
  readonly group: string | null;
  readonly venue: string | null;
  readonly scheduledAt: string | null;
  readonly scorerId: UserId | null;
  readonly status: FixtureStatus;
  readonly summaryLine: string | null;
  readonly resultText: string | null;
}

export interface GenerateFixturesRequest {
  readonly venues: readonly string[];
}

export interface GenerateFixturesResponse {
  readonly fixtures: readonly Fixture[];
}

export interface UpdateFixtureRequest {
  readonly venue?: string | null;
  readonly scheduledAt?: string | null;
  readonly status?: "abandoned" | "no_result";
}

export interface AssignScorerRequest {
  readonly matchId: MatchId;
  readonly userId: UserId;
}

// ── Standings and leaderboards (P1, #37) ───────────────────────────────

export interface StandingsRow {
  readonly teamId: TeamId;
  readonly teamName: string;
  readonly played: number;
  readonly won: number;
  readonly lost: number;
  readonly noResult: number;
  readonly points: number;
  readonly nrr: number;
  readonly group: string | null;
}

export interface StandingsResponse {
  readonly standings: readonly StandingsRow[];
}

export type LeaderboardCategory = "most_runs" | "most_wickets" | "best_strike_rate" | "best_economy";

export interface LeaderboardEntry {
  readonly playerId: PlayerId;
  readonly playerName: string;
  readonly teamName: string;
  readonly matches: number;
  readonly value: number;
  readonly supporting: string;
}

export interface LeaderboardResponse {
  readonly category: LeaderboardCategory;
  readonly entries: readonly LeaderboardEntry[];
}

export interface WhatIfRequest {
  readonly matchId: MatchId;
  readonly teamAScore: number;
  readonly teamAOvers: number;
  readonly teamAWickets: number;
  readonly teamBScore: number;
  readonly teamBOvers: number;
  readonly teamBWickets: number;
}

export interface WhatIfResponse {
  readonly standings: readonly StandingsRow[];
}

// ── Player stats and identity (P1, #37) ────────────────────────────────

export interface BattingStats {
  readonly matches: number;
  readonly innings: number;
  readonly runs: number;
  readonly highScore: number;
  readonly highScoreNotOut: boolean;
  readonly average: number | null;
  readonly strikeRate: number | null;
  readonly fifties: number;
  readonly hundreds: number;
  readonly boundaryPct: number | null;
  readonly dotBallPct: number | null;
}

export interface BowlingStats {
  readonly matches: number;
  readonly innings: number;
  readonly wickets: number;
  readonly bestBowling: string;
  readonly average: number | null;
  readonly economy: number | null;
  readonly strikeRate: number | null;
  readonly fiveWickets: number;
}

export interface FieldingStats {
  readonly catches: number;
  readonly stumpings: number;
  readonly runOuts: number;
}

export interface RecentInnings {
  readonly matchId: MatchId;
  readonly runs: number;
  readonly balls: number;
  readonly notOut: boolean;
  readonly oppositionName: string;
  readonly date: string;
}

/**
 * A team on a player's profile.
 *
 * Deliberately not `Team`: that carries the full squad, and each squad member carries a
 * phone and an email. Rendering a list of a stranger's clubs must not ship a contact book
 * with it, so this names the team and stops.
 */
export interface PlayerTeam {
  readonly teamId: TeamId;
  readonly name: string;
  readonly squadSize: number;
}

/**
 * A match a player was picked for.
 *
 * "Was in the playing eleven", not "batted or bowled" — a number eleven in a game that
 * ended early played, and defining it by event evidence erases him.
 */
export interface PlayerMatch {
  readonly matchId: MatchId;
  readonly shortcode: Shortcode;
  readonly teamName: string;
  readonly oppositionName: string;
  readonly status: MatchStatus;
  readonly resultText: string | null;
  readonly playedAt: string;
}

export interface PlayerStatsResponse {
  readonly playerId: PlayerId;
  readonly displayName: string;
  readonly batting: BattingStats;
  readonly bowling: BowlingStats;
  readonly fielding: FieldingStats;
  readonly recentInnings: readonly RecentInnings[];
  readonly teams: readonly PlayerTeam[];
  readonly matches: readonly PlayerMatch[];
  /** How they bat and bowl (ADR-0023). Absent from a response cached by an older build. */
  readonly style?: PlayerStyle;
}

export interface ClaimPlayerRequest {
  readonly playerId: PlayerId;
}

export interface MergePlayersRequest {
  readonly keepPlayerId: PlayerId;
  readonly mergePlayerId: PlayerId;
}

// ── Push notifications (P1, #37) ───────────────────────────────────────

export interface PushSubscribeRequest {
  readonly matchId: MatchId;
  readonly endpoint: string;
  readonly keys: { readonly p256dh: string; readonly auth: string };
}

/**
 * The application server's public key, for `pushManager.subscribe`.
 *
 * Served rather than baked into the bundle, so the key is one deployed fact rather than one
 * compiled into every build. `null` means push is not configured — a real answer, not an
 * error, and the client hides the affordance rather than offering something that cannot work.
 *
 * Note what this does **not** buy: rotating the VAPID pair is not a cheap operation. A browser
 * subscription is bound to the `applicationServerKey` it was created with, so after a rotation
 * every existing subscription is undeliverable and re-subscribing on a device still holding
 * the old one throws. Rotation means pruning the table and having people opt in again.
 */
export interface PushKeyResponse {
  readonly publicKey: string | null;
}

// ── Live match discovery (P1, #37) ─────────────────────────────────────

/**
 * Everyone's public matches in one state: `liveMatches` for what is in progress,
 * `recentMatches` for what finished most recently. Unauthenticated.
 */
export interface LiveMatchesResponse {
  readonly matches: readonly MatchListItem[];
}

// ── Streaming a match to YouTube (#114) ───────────────────────────────────────

/** Someone who may hold a match's camera, as the creator sees them. */
export interface StreamerSummary {
  readonly userId: UserId;
  readonly displayName: string;
}

/** `PUT /api/matches/:id/streamer`. `null` clears it, which hands streaming back to the creator. */
export interface SetStreamerRequest {
  readonly userId: UserId | null;
}

export interface MatchStreamer {
  readonly matchId: MatchId;
  readonly streamer: StreamerSummary | null;
}

/**
 * A person in the choose-streamer sheet.
 *
 * Squad players with no account are listed so the creator sees why they are missing from
 * the pickable set, with `userId: null` and `canStream: false`: streaming publishes to our
 * channel, so whoever streams must be able to sign in.
 */
export interface StreamerCandidate {
  readonly userId: UserId | null;
  readonly displayName: string;
  /** The squad they are in; `null` for a creator who is in neither. */
  readonly teamName: string | null;
  /** Never the whole number — `+91 ••••• 3210`. */
  readonly maskedPhone: string | null;
  readonly isCreator: boolean;
  readonly canStream: boolean;
}

export interface StreamerCandidatesResponse {
  readonly candidates: readonly StreamerCandidate[];
}

/**
 * A match on the streamer app's home screen.
 *
 * `role` says why it is listed; `canGoLive` says whether *this* user may start the stream,
 * which is false for a creator who assigned someone else.
 */
export interface StreamableMatch {
  readonly id: MatchId;
  readonly shortcode: Shortcode;
  readonly teamAName: string;
  readonly teamBName: string;
  readonly role: "created" | "assigned";
  readonly canGoLive: boolean;
  readonly streamer: StreamerSummary | null;
  /** Something is in the ledger — the innings has begun. */
  readonly inPlay: boolean;
  /** False goes out unlisted on YouTube. */
  readonly public: boolean;
  /**
   * The broadcast this user is live on for this match, if any — so an app reopened after being
   * killed mid-stream can offer to resume it. Going live again returns that same broadcast.
   */
  readonly myLiveBroadcastId: string | null;
  readonly tournamentName: string | null;
  readonly venue: string | null;
  /** From the tournament fixture; standalone matches have no schedule. */
  readonly scheduledAt: string | null;
}

/** A finished stream of mine, with its replay. */
export interface RecentStream {
  readonly matchId: MatchId;
  readonly teamAName: string;
  readonly teamBName: string;
  /** With `endedAt`, how long it ran ("2h 41m"). */
  readonly startedAt: string | null;
  readonly endedAt: string;
  readonly watchUrl: string;
}

export type StreamQuality = "720p" | "480p";

/** `GET /api/app/version`: below `minAppVersion` the streamer app shows "Update the app" (#128). */
export interface AppVersionResponse {
  /** The Android versionCode of the oldest streamer app this server still works with. */
  readonly minAppVersion: number;
}

/** `POST /api/matches/:id/broadcast`. */
export interface GoLiveRequest {
  readonly quality: StreamQuality;
}

/**
 * What the streamer's phone needs to start sending video.
 *
 * `streamKey` is a secret: anyone holding it can broadcast on our channel. It is returned only
 * here, to the one person who may stream the match, and never by any GET (ADR-0019). The same
 * streamer asking again gets the same broadcast back — that is how a killed app rejoins its
 * video rather than starting a second one.
 */
export interface GoLiveResponse {
  readonly broadcastId: string;
  readonly ingestUrl: string;
  readonly streamKey: string;
  readonly watchUrl: string;
  /**
   * The quality the broadcast was created at. On a resume it is the original, not the one just
   * asked for, so the encoder restarts at the size YouTube already has.
   */
  readonly quality: StreamQuality;
}

/** `POST /api/broadcasts/:id/end`. Optional: an old app sends no body. */
export interface EndBroadcastRequest {
  /** How often the phone had to reconnect during the stream. */
  readonly reconnects?: number;
}

/**
 * `GET /api/matches/:id/broadcast` — public, no key. `ended` keeps the watch link, because
 * the ended broadcast is the replay.
 */
export interface BroadcastStatusResponse {
  readonly status: "none" | "live" | "ended";
  readonly watchUrl: string | null;
  readonly startedAt: string | null;
  readonly endedAt: string | null;
  /**
   * How many are watching, while live: read from YouTube at most every 30 s. Null when unknown
   * — not live, the channel hides it, or YouTube did not answer — never a made-up 0.
   */
  readonly viewers: number | null;
  /** When `viewers` was read; null exactly when `viewers` is. */
  readonly viewersAt: string | null;
}

/**
 * One stretch of time during which the phone was connected to YouTube's ingest. Times are the
 * phone's own clock; the server corrects them with the skew it measures on each report.
 */
export interface StreamSegment {
  readonly startedAt: string;
  /** Null while this connection is still up. */
  readonly endedAt: string | null;
}

/**
 * `PUT /api/broadcasts/:id/timeline` — every connection the phone has seen on this broadcast so
 * far (ADR-0021). Sent whole each time, so a lost or repeated report changes nothing; segments are
 * keyed by their start, so an app restarted mid-stream adds to the old ones rather than
 * replacing them.
 */
export interface StreamTimelineRequest {
  /** The phone's clock as it sent this, which is how the server measures the phone's skew. */
  readonly sentAt: string;
  readonly segments: readonly StreamSegment[];
}

/** One delivery, placed on the match's YouTube video (ADR-0021). The training corpus's row. */
export interface BallVideoMark {
  /** The version of the ball that applies — corrections already folded in. */
  readonly eventId: EventId;
  readonly inningsNo: InningsNo;
  /** The ball's permanent address in the innings (ADR-0004). */
  readonly seq: number;
  /** Zero-based over, and the delivery's place in it: `over 12, ball 4` reads `12.4`. */
  readonly over: number;
  readonly ballInOver: number;
  /** What the over strip shows: `4`, `W`, `1wd`. */
  readonly chip: string;
  readonly runs: number;
  readonly wicket: boolean;
  readonly legal: boolean;
  /**
   * When the scorer tapped the ball in — **after** it was bowled, usually by 3–10 s. The
   * delivery itself sits in the seconds before this on the video.
   */
  readonly scoredAt: string;
  /** The broadcast that was on air when it was scored, or null when none was. */
  readonly broadcastId: string | null;
  /**
   * Seconds into that YouTube video at `scoredAt`. Null when the phone was not connected at
   * that moment (the delivery may still be on video, just before a drop).
   */
  readonly videoSec: number | null;
  /** The replay opened a few seconds before `scoredAt`, for a person checking by eye. */
  readonly watchUrlAt: string | null;
}

export interface BallVideoStream {
  readonly broadcastId: string;
  readonly youtubeVideoId: string | null;
  readonly watchUrl: string | null;
  /** On the server's clock. */
  readonly segments: readonly StreamSegment[];
}

/**
 * `GET /api/matches/:id/ball-video` — every delivery with where it is on the video (ADR-0021).
 * Worked out on each read from the stored times, never stored itself, so a better formula
 * later re-labels every match already played.
 */
export interface BallVideoResponse {
  readonly matchId: MatchId;
  readonly streams: readonly BallVideoStream[];
  readonly balls: readonly BallVideoMark[];
}

export interface StreamableMatchesResponse {
  /** In play first, then upcoming by schedule, then the rest newest first. */
  readonly matches: readonly StreamableMatch[];
  readonly recent: readonly RecentStream[];
}

// ── Live delivery (#8) ──────────────────────────────────────────────────────

/**
 * One SSE frame. Deliberately a small delta rather than a `MatchState`, because
 * PRD §7.3 budgets under 1 KB per update — a budget met structurally by patching
 * an already-rendered page, not by optimising a re-render.
 */
export interface LiveUpdate {
  /**
   * Position in the ledger, innings included. A bare `seq` would restart at 1 each
   * innings, so a client dropping frames with `seq <= lastSeen` — the obvious
   * implementation — would silently discard every ball of the second innings and
   * freeze the public page at the innings break.
   */
  readonly inningsNo: InningsNo;
  readonly seq: number;
  readonly runs: number;
  readonly wickets: number;
  readonly overs: OverCount;
  /**
   * The side batting this innings. Added for the scoreboard drawn into the stream (ADR-0021);
   * optional because a frame buffered before it existed has none.
   */
  readonly battingTeamName?: string | null;
  /** `87/3`, `187` all out — formatted here so the app draws exactly what the page shows. */
  readonly scoreText?: string;
  /** `12.4`. */
  readonly oversText?: string;
  readonly strikerName: string | null;
  readonly nonStrikerName: string | null;
  readonly bowlerName: string | null;
  readonly currentRunRate: number | null;
  readonly requiredRunRate: number | null;
  readonly lastBall: string | null;
  /**
   * The over the last ball belongs to, zero-based.
   *
   * Carried so the client can append the chip to the *right* over on the public page. It
   * cannot be derived from `overs`: a wide bowled as the seventh delivery of an over leaves
   * `overs` reading `x.0` of the next over while the chip belongs to the previous one, which
   * is exactly the ball a naive client files in the wrong row.
   */
  readonly over: number;
  readonly statusLine: string;
  /**
   * `[label, text]` pairs: write `text` into the node carrying `data-live="label"`.
   *
   * The page's whole patching instruction, and the reason the client ships no cricket logic at
   * all — it writes strings into labelled nodes and does nothing else. Every value is formatted
   * by `@sportsos/engine` on the way out, so a live page cannot spell a number differently from
   * the document it is patching (ADR-0006).
   *
   * It exists because a header-only patch left the page **contradicting itself**: the score read
   * `36/0` while the card beneath still said `4/0` and `Arjun 4 (1)`. A page that disagrees with
   * itself is worse than one merely stale — "the header disagreeing with the scorecard beneath
   * it" is the exact failure ADR-0006 was written to prevent.
   *
   * A list rather than fixed fields, so adding a patched node later costs a server change and
   * nothing on the client — which matters when the client is a hand-written 2 KB.
   */
  readonly patch: readonly (readonly [string, string])[];
  /**
   * The card rows this ball changed, keyed by player.
   *
   * Separate from `patch` purely for size. Spelling every cell as its own `[label, text]` pair
   * put a 36-character uuid in seven keys per batter and pushed the frame to 1214 bytes —
   * over PRD §7.3's 1 KB. One id per row and short field names bring it back under, and the
   * budget is the requirement, not the shape.
   *
   * `p` is the player id; the rest are the cells. Pre-formatted where formatting matters
   * (`sr`, `figures`), so the client still assembles nothing.
   */
  readonly rows: readonly LiveRow[];
}

/** One changed card row. Field names are short because they are on the wire per ball. */
export interface LiveRow {
  /** Player id. The page labels each cell `bat-*-<p>` / `bowl-<p>`, so a patch cannot land on
   *  the wrong line even if the table is reordered. */
  readonly p: string;
  /** Batting: name with the striker's `*`. */
  readonly n?: string;
  readonly r?: number;
  readonly b?: number;
  readonly f?: number;
  readonly s?: number;
  /** Strike rate, already `-` when undefined rather than `0.00` or `NaN`. */
  readonly sr?: string;
  /** Bowling: `4-0-32-2 (8.00)`. */
  readonly figures?: string;
}

// ── Endpoint map ────────────────────────────────────────────────────────────

/**
 * Every route, paired with its request and response types.
 *
 * `ROUTES` alone was a bare name→string map, which did not actually make a
 * mismatch a compile error — nothing associated `appendEvents` with
 * `AppendEventsRequest`. This map does, so a client helper generic over
 * `RouteName` cannot send or expect the wrong shape.
 *
 * `never` means the route takes no body.
 */
export interface RouteContract {
  signUp: { req: SignUpRequest; res: SessionResponse };
  signIn: { req: SignInRequest; res: SessionResponse };
  signOut: { req: never; res: OkResponse };
  googleStart: { req: never; res: never };
  googleCallback: { req: never; res: never };
  exchangeAppCode: { req: AppCodeExchangeRequest; res: SessionResponse };
  /** Android's app-link verification statement (#128). JSON, not the API envelope. */
  assetLinks: { req: never; res: unknown };
  /** Server-rendered HTML: the app link's landing page when Android did not open the app. */
  appLinkLanding: { req: never; res: string };
  me: { req: never; res: MeResponse };
  sendOtp: { req: SendOtpRequest; res: OkResponse };
  verifyOtp: { req: VerifyOtpRequest; res: SessionResponse };
  updateProfile: { req: UpdateProfileRequest; res: MeResponse };

  createPlayer: { req: CreatePlayerRequest; res: Player };
  searchPlayers: { req: never; res: PlayersResponse };
  resolvePlayer: { req: ResolvePlayerRequest; res: Player };
  createTeam: { req: CreateTeamRequest; res: Team };
  listTeams: { req: never; res: TeamsResponse };
  addTeamPlayers: { req: AddTeamPlayersRequest; res: Team };
  createTeamInvite: { req: CreateTeamInviteRequest; res: TeamInviteResponse };
  getTeamInvite: { req: never; res: TeamInviteResponse };
  revokeTeamInvite: { req: never; res: OkResponse };
  previewInvite: { req: never; res: InvitePreviewResponse };
  joinTeam: { req: never; res: JoinTeamResponse };
  createRuleSet: { req: CreateRuleSetRequest; res: RuleSet };
  listRuleSets: { req: never; res: RuleSetsResponse };

  createMatch: { req: CreateMatchRequest; res: Match };
  listMatches: { req: never; res: MatchesResponse };
  getMatch: { req: never; res: Match };
  updateSquad: { req: UpdateSquadRequest; res: Match };
  setMatchVisibility: { req: SetMatchVisibilityRequest; res: Match };
  liveMatches: { req: never; res: LiveMatchesResponse };
  recentMatches: { req: never; res: LiveMatchesResponse };

  getEvents: { req: never; res: GetEventsResponse };
  appendEvents: { req: AppendEventsRequest; res: AppendEventsResponse };

  completeMatch: { req: never; res: CompleteMatchResponse };
  reopenMatch: { req: ReopenMatchRequest; res: OkResponse };

  // ── Tournaments ────────────────────────────────────────────────────
  createTournament: { req: CreateTournamentRequest; res: Tournament };
  listTournaments: { req: never; res: TournamentsResponse };
  getTournament: { req: never; res: TournamentDetailResponse };
  updateTournament: { req: Partial<CreateTournamentRequest>; res: Tournament };
  addTournamentTeams: { req: AddTournamentTeamsRequest; res: OkResponse };
  generateFixtures: { req: GenerateFixturesRequest; res: GenerateFixturesResponse };
  updateFixture: { req: UpdateFixtureRequest; res: Fixture };
  getStandings: { req: never; res: StandingsResponse };
  getLeaderboard: { req: never; res: LeaderboardResponse };
  whatIfNRR: { req: WhatIfRequest; res: WhatIfResponse };
  assignScorer: { req: AssignScorerRequest; res: OkResponse };
  /** Server-rendered HTML, not JSON (ADR-0007). */
  publicTournamentPage: { req: never; res: string };

  // ── Player stats and identity ──────────────────────────────────────
  getPlayerStats: { req: never; res: PlayerStatsResponse };
  claimPlayer: { req: ClaimPlayerRequest; res: OkResponse };
  mergePlayers: { req: MergePlayersRequest; res: OkResponse };
  updatePlayerStyle: { req: UpdatePlayerStyleRequest; res: PlayerStyle };

  // ── Push notifications ─────────────────────────────────────────────
  pushKey: { req: never; res: PushKeyResponse };
  pushSubscribe: { req: PushSubscribeRequest; res: OkResponse };
  pushUnsubscribe: { req: never; res: OkResponse };

  /** Server-sent events, so the response is a stream of `LiveUpdate`. */
  liveStream: { req: never; res: LiveUpdate };

  // ── Streaming to YouTube ───────────────────────────────────────────
  setStreamer: { req: SetStreamerRequest; res: MatchStreamer };
  streamerCandidates: { req: never; res: StreamerCandidatesResponse };
  streamableMatches: { req: never; res: StreamableMatchesResponse };
  goLive: { req: GoLiveRequest; res: GoLiveResponse };
  appVersion: { req: never; res: AppVersionResponse };
  broadcastStatus: { req: never; res: BroadcastStatusResponse };
  endBroadcast: { req: EndBroadcastRequest; res: OkResponse };
  streamTimeline: { req: StreamTimelineRequest; res: OkResponse };
  ballVideo: { req: never; res: BallVideoResponse };
  /** Server-rendered HTML, not JSON. */
  publicMatchPage: { req: never; res: string };
  /**
   * The front door: `GET /`, a server-rendered marketing document (ADR-0014).
   *
   * In the contract even though no client ever calls it, because the contract is what stops
   * the server registering a handler at a path the client does not know about — and `/` is
   * the one path where a drift is invisible, since the static fallback would answer it with
   * the app and everyone holding a session would see nothing wrong.
   */
  landingPage: { req: never; res: string };
  /**
   * `GET /privacy`, a server-rendered legal document.
   *
   * In the contract for the same reason `landingPage` is: without an entry here the static
   * fallback would answer the path with the SPA shell, and a reviewer — or Google's consent
   * screen — would be pointed at an empty page that returns 200 and looks fine in a browser
   * holding a session.
   */
  privacyPage: { req: never; res: string };
}

export type RouteRequest<N extends RouteName> = RouteContract[N]["req"];
export type RouteResponse<N extends RouteName> = RouteContract[N]["res"];

/** The wire path for each route. Keys must match `RouteContract` exactly. */
/**
 * Where the *browser* goes, as opposed to where the client calls.
 *
 * `ROUTES` below is the API surface. This is the short list of app screens that something
 * other than the SPA's own router needs to name — today that is the server, which has to send
 * a browser somewhere after the Google callback.
 *
 * It is here for the reason `ROUTES` is here. `/` used to be the app, so the auth routes
 * redirected there after a Google sign-in and it worked; ADR-0014 moved the app to `/matches`
 * and the redirect kept pointing at `/`, which is now a marketing document. The scorer signed
 * in successfully and was shown a page saying "Sign in" — and on the failure path the document
 * ignored the `?auth=failed&reason=` the SPA reads, so Google errors went silent too. One
 * copy of these strings, shared, is what stops that recurring.
 */
export const APP_PATHS = {
  /** The signed-in match list. Was `/` until ADR-0014. */
  home: "/matches",
  signIn: "/signin",
  signUp: "/signup",
} as const;

export const ROUTES = {
  // The front door. Matched ahead of the SPA's static fallback, which is what makes `/` a
  // document rather than the app (ADR-0014).
  landingPage: "GET /",

  // Ahead of the SPA fallback for the same reason as `/`. The consent screen of a Google
  // Cloud project links here, so this path must answer with the policy for as long as that
  // project exists — it is a published address, not an internal one.
  privacyPage: "GET /privacy",

  signUp: "POST /api/auth/signup",
  signIn: "POST /api/auth/signin",
  signOut: "POST /api/auth/signout",
  googleStart: "GET /api/auth/google",
  googleCallback: "GET /api/auth/google/callback",
  /** The streamer app's half of Google sign-in (ADR-0020). */
  exchangeAppCode: "POST /api/auth/app/exchange",
  /** Android verifies the streamer app's https links against this (#128, ADR-0020). */
  assetLinks: "GET /.well-known/assetlinks.json",
  /** The verified app link Google sign-in returns through; Android opens the app for it. */
  appLinkLanding: "GET /app/oauth",
  me: "GET /api/me",
  sendOtp: "POST /api/auth/otp/send",
  verifyOtp: "POST /api/auth/otp/verify",
  updateProfile: "PUT /api/me",

  createPlayer: "POST /api/players",
  searchPlayers: "GET /api/players",
  resolvePlayer: "POST /api/players/resolve",
  createTeam: "POST /api/teams",
  listTeams: "GET /api/teams",
  addTeamPlayers: "POST /api/teams/:id/players",
  createTeamInvite: "POST /api/teams/:id/invite",
  getTeamInvite: "GET /api/teams/:id/invite",
  revokeTeamInvite: "DELETE /api/teams/:id/invite",
  previewInvite: "GET /api/join/:code",
  joinTeam: "POST /api/join/:code",
  createRuleSet: "POST /api/rulesets",
  listRuleSets: "GET /api/rulesets",

  createMatch: "POST /api/matches",
  listMatches: "GET /api/matches",
  getMatch: "GET /api/matches/:id",
  updateSquad: "PUT /api/matches/:id/squad",
  setMatchVisibility: "PUT /api/matches/:id/visibility",
  liveMatches: "GET /api/matches/live",
  recentMatches: "GET /api/matches/recent",

  getEvents: "GET /api/matches/:id/events",
  appendEvents: "POST /api/matches/:id/events",

  completeMatch: "POST /api/matches/:id/complete",
  reopenMatch: "POST /api/matches/:id/reopen",

  // ── Tournaments (P1) ──────────────────────────────────────────────
  createTournament: "POST /api/tournaments",
  listTournaments: "GET /api/tournaments",
  getTournament: "GET /api/tournaments/:id",
  updateTournament: "PUT /api/tournaments/:id",
  addTournamentTeams: "POST /api/tournaments/:id/teams",
  generateFixtures: "POST /api/tournaments/:id/fixtures",
  updateFixture: "PUT /api/tournaments/:id/fixtures/:matchId",
  getStandings: "GET /api/tournaments/:id/standings",
  getLeaderboard: "GET /api/tournaments/:id/leaderboards",
  whatIfNRR: "POST /api/tournaments/:id/whatif",
  assignScorer: "POST /api/tournaments/:id/assign-scorer",
  /** Server-rendered, unauthenticated, zero client React (ADR-0007). */
  publicTournamentPage: "GET /t/:shortcode",

  // ── Player stats and identity (P1) ────────────────────────────────
  getPlayerStats: "GET /api/players/:id/stats",
  claimPlayer: "POST /api/players/:id/claim",
  mergePlayers: "POST /api/players/merge",
  updatePlayerStyle: "PUT /api/players/:id/style",

  // ── Push notifications (P1) ───────────────────────────────────────
  pushKey: "GET /api/notifications/key",
  pushSubscribe: "POST /api/notifications/subscribe",
  pushUnsubscribe: "DELETE /api/notifications/subscribe/:matchId",

  liveStream: "GET /api/matches/:id/stream",

  // ── Streaming to YouTube (#114) ───────────────────────────────────
  // `/stream` above is the live *score* (SSE); the video lives under these names instead.
  setStreamer: "PUT /api/matches/:id/streamer",
  streamerCandidates: "GET /api/matches/:id/streamer-candidates",
  streamableMatches: "GET /api/streamable-matches",
  goLive: "POST /api/matches/:id/broadcast",
  appVersion: "GET /api/app/version",
  broadcastStatus: "GET /api/matches/:id/broadcast",
  endBroadcast: "POST /api/broadcasts/:id/end",
  streamTimeline: "PUT /api/broadcasts/:id/timeline",
  ballVideo: "GET /api/matches/:id/ball-video",

  /** Server-rendered, unauthenticated, zero client React (ADR-0007). */
  publicMatchPage: "GET /m/:shortcode",
} as const;

export type RouteName = keyof typeof ROUTES;

/**
 * Compile-time guard that `ROUTES` and `RouteContract` describe the same set. Add a
 * route to one and forget the other and this line fails to typecheck, which is the
 * only thing keeping the two in step.
 */
type RoutesMatchContract = RouteName extends keyof RouteContract
  ? keyof RouteContract extends RouteName
    ? true
    : never
  : never;
const _routesMatchContract: RoutesMatchContract = true;
void _routesMatchContract;
